import React, { Component, ErrorInfo, ReactNode } from "react";
import { Alert, Button, Container, Typography, Box } from "@mui/material";
import { RefreshRounded as RefreshIcon } from "@mui/icons-material";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={this.handleRefresh}
              >
                Refresh Page
              </Button>
            }
          >
            <Typography variant="h6" component="div">
              Something went wrong
            </Typography>
            <Typography variant="body2">
              An unexpected error occurred. Please try refreshing the page.
            </Typography>
          </Alert>

          {process.env.NODE_ENV === "development" && this.state.error && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Error Details (Development Mode):
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {this.state.error.toString()}
                </Typography>
              </Alert>
              {this.state.errorInfo && (
                <Alert severity="info">
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {this.state.errorInfo.componentStack}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
